/**
 * Lift2Fit — All Programmes: every block a lifter has, with what it was and what
 * it produced.
 *
 * One list for both audiences: the lifter sees it on their Programme page and on
 * the dedicated `#/programme/all` page, and a coach sees the same component
 * read-only for a client — the server already applies the link guard, so this
 * component never decides who may read what.
 *
 * Two densities, one source:
 *   * `lean` is the inline list under the Programme page — name, dates, cadence
 *     and the actions only. The per-block numbers moved to the All page so the
 *     Programme page is visibly shorter and nothing was lost.
 *   * the full density (the default) is the All page: when the block ran, how
 *     long it was, how often per week, how many sessions/sets/days were actually
 *     logged, and which block it came from. Activating one re-archives whatever
 *     is running; history is never rewritten.
 */
import { useState } from 'react';
import type { PlanHistoryEntry } from '@/lib/api';
import { formatDate, pluralize } from '@/lib/format';
import { ArrowRightIcon, CheckIcon, LockIcon } from '@/lib/icons';
import { Link } from './router';
import { Panel, StatusChip } from './components';
import { blockRangeLabel } from './new-block';

/**
 * "Copied from …" — honest about a source this table cannot show. The coach's
 * list is coach-owned only, so a copy made from the lifter's own block has a
 * lineage id that will never be a row here; that is not the same as "removed".
 */
function lineageLabel(
  entry: PlanHistoryEntry,
  source: PlanHistoryEntry | null,
  audience: 'lifter' | 'coach',
): string {
  if (!entry.copiedFromPlanId) return 'Started from scratch';
  if (source) return `Copied from ${source.name}`;
  return audience === 'coach' ? 'Copied from a block outside this list' : 'Copied from a removed block';
}

/** A coach block waiting for the lifter: not running, not history yet. */
export function isReadyCoachBlock(entry: PlanHistoryEntry): boolean {
  return entry.coachOwned === true && !entry.isActive && !entry.archivedAt;
}

export function AllProgrammesPanel({
  blocks,
  audience,
  loading = false,
  busyId = null,
  disabled = false,
  onView,
  onActivate,
  onDeactivate,
  onDelete,
  title = 'All Programmes',
  subtitle,
  /** The inline density: drop the per-block numbers (they live on the All page). */
  lean = false,
  /** The "All" action at the far end of the panel header, e.g. `/programme/all`. */
  allHref,
}: {
  blocks: readonly PlanHistoryEntry[];
  audience: 'lifter' | 'coach';
  loading?: boolean;
  busyId?: string | null;
  /** True while any write on the screen is in flight. */
  disabled?: boolean;
  onView?: (entry: PlanHistoryEntry) => void;
  onActivate?: (entry: PlanHistoryEntry) => void;
  /** Stop the running block without replacing it ("nothing runs for now"). */
  onDeactivate?: (entry: PlanHistoryEntry) => void;
  /** Delete an INACTIVE block, after the in-row confirmation. */
  onDelete?: (entry: PlanHistoryEntry) => void;
  title?: string;
  subtitle?: string;
  lean?: boolean;
  allHref?: string;
}) {
  const byId = new Map(blocks.map((block) => [block.planId, block]));
  const actionable = Boolean(onView || onActivate || onDeactivate || onDelete);
  // The delete confirmation lives in the row, not a browser dialog: the question
  // and the two answers are part of the page.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  return (
    <Panel
      testId="all-programmes"
      title={title}
      subtitle={
        subtitle ??
        (audience === 'lifter'
          ? 'Every block you have had, newest first. Activate one to make it the plan the Grid starts from — your history stays intact.'
          : 'Every block YOU wrote for this client, newest first. Activate one to put it back on the Grid; the record of what ran stays intact. The client’s own blocks are theirs, and never listed here.')
      }
      actions={
        allHref ? (
          <Link
            to={allHref}
            data-testid="all-programmes-all"
            className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            All
            <ArrowRightIcon className="text-[11px]" />
          </Link>
        ) : null
      }
    >
      {loading && blocks.length === 0 ? (
        <p className="text-[13px] text-ink-muted">Loading your blocks…</p>
      ) : blocks.length === 0 ? (
        <p className="text-[13px] text-ink-muted">
          {audience === 'lifter'
            ? 'No blocks yet — this is your first one.'
            : 'You have not written a block for this client yet.'}
        </p>
      ) : (
        <ul className="flex flex-col">
          {blocks.map((entry) => {
            const source = entry.copiedFromPlanId ? byId.get(entry.copiedFromPlanId) : null;
            const ready = isReadyCoachBlock(entry);
            const readyFrom = entry.startsOn ?? entry.createdAt;
            return (
              <li
                key={entry.planId}
                data-testid={`all-programmes-${entry.planId}`}
                className="flex flex-col gap-3 border-b border-hairline py-3 last:border-b-0"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[14px] font-bold text-ink">{entry.name}</span>
                      {entry.isActive ? (
                        <StatusChip tone="success" icon={<CheckIcon />}>
                          Active
                        </StatusChip>
                      ) : null}
                      {entry.coachOwned ? (
                        <StatusChip tone="info" icon={<LockIcon />}>
                          Your coach&rsquo;s
                        </StatusChip>
                      ) : null}
                    </span>
                    <span className="text-[12px] text-ink-muted" data-testid={`all-programmes-meta-${entry.planId}`}>
                      {ready
                        ? // A coach's block reaches the lifter without stealing
                          // their session: it is "new from <date> · from your coach"
                          // until they choose to start it.
                          `new from ${readyFrom ? formatDate(readyFrom) : 'today'}${
                            audience === 'lifter' ? ' · from your coach' : ''
                          }`
                        : blockRangeLabel(entry)}{' '}
                      · {entry.trackLabel} · {pluralize(entry.weeks, 'week')} ·{' '}
                      {pluralize(entry.daysPerWeek, 'day')}/week
                    </span>
                  </span>
                  {actionable ? (
                    <span className="flex flex-wrap items-center gap-2">
                      {onView ? (
                        <button
                          type="button"
                          data-testid={`view-${entry.planId}`}
                          onClick={() => onView(entry)}
                          disabled={disabled}
                          className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
                        >
                          {audience === 'lifter' ? 'View' : 'Open'}
                        </button>
                      ) : null}
                      {/* Activate is for EVERYONE, including a lifter switching back to
                          their coach's block: choosing which block runs is the
                          lifter's decision, and it stays read-only after activation.
                          The old gate hid the button for coach-owned blocks, which
                          strands a lifter who activated their own. */}
                      {entry.isActive ? (
                        onDeactivate ? (
                          <button
                            type="button"
                            data-testid={`deactivate-${entry.planId}`}
                            disabled={disabled || busyId === entry.planId}
                            onClick={() => onDeactivate(entry)}
                            className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
                          >
                            Deactivate
                          </button>
                        ) : onActivate ? (
                          <button
                            type="button"
                            data-testid={`activate-${entry.planId}`}
                            disabled
                            className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink disabled:opacity-40"
                          >
                            <ArrowRightIcon className="text-[11px]" />
                            Running
                          </button>
                        ) : null
                      ) : onActivate ? (
                        <button
                          type="button"
                          data-testid={`activate-${entry.planId}`}
                          disabled={disabled || busyId === entry.planId}
                          onClick={() => onActivate(entry)}
                          className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
                        >
                          <ArrowRightIcon className="text-[11px]" />
                          Activate
                        </button>
                      ) : null}
                      {/* Delete is the coach's for a coach-owned block, the lifter's
                          for their own; an ACTIVE block cannot be deleted (the API
                          refuses it) — deactivate first, so the button says so. */}
                      {onDelete && (audience === 'coach' || entry.coachOwned !== true) ? (
                        entry.isActive ? (
                          <button
                            type="button"
                            data-testid={`delete-${entry.planId}`}
                            disabled
                            title="Deactivate it first"
                            className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink disabled:opacity-40"
                          >
                            Delete
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-testid={`delete-${entry.planId}`}
                            disabled={disabled || busyId === entry.planId}
                            onClick={() => setConfirmDeleteId(entry.planId)}
                            className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-danger hover:text-danger disabled:opacity-40"
                          >
                            Delete
                          </button>
                        )
                      ) : null}
                    </span>
                  ) : null}
                </div>

                {confirmDeleteId === entry.planId ? (
                  <div
                    data-testid={`delete-confirm-${entry.planId}`}
                    className="flex flex-wrap items-center gap-2 border border-danger/40 bg-danger/10 px-3 py-2"
                  >
                    <span className="text-[12px] font-bold text-danger">
                      Are you sure you want to delete this block?
                    </span>
                    <button
                      type="button"
                      data-testid={`delete-confirm-yes-${entry.planId}`}
                      disabled={disabled || busyId === entry.planId}
                      onClick={() => {
                        setConfirmDeleteId(null);
                        onDelete?.(entry);
                      }}
                      className="inline-flex h-9 items-center border border-danger px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-danger hover:bg-danger hover:text-white disabled:opacity-40"
                    >
                      Delete block
                    </button>
                    <button
                      type="button"
                      data-testid={`delete-cancel-${entry.planId}`}
                      onClick={() => setConfirmDeleteId(null)}
                      className="inline-flex h-9 items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                    >
                      Cancel
                    </button>
                  </div>
                ) : null}

                {lean ? null : (
                  <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid={`all-programmes-detail-${entry.planId}`}>
                    {(
                      [
                        ['Sessions', String(entry.sessionsCompleted ?? 0)],
                        ['Days logged', String(entry.daysLogged ?? 0)],
                        ['Sets logged', String(entry.setsLogged ?? 0)],
                        ['Lineage', lineageLabel(entry, source ?? null, audience)],
                      ] as Array<[string, string]>
                    ).map(([label, value]) => (
                      <div key={label} className="flex flex-col gap-0.5 border border-hairline bg-page px-2 py-1.5">
                        <dt className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">{label}</dt>
                        <dd className="text-[12px] font-bold text-ink">{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {entry.archivedAt ? (
                  <p className="text-[11px] text-ink-muted">
                    Replaced {formatDate(entry.archivedAt)} — activating it again keeps the record of this run.
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
